import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { QrCodeIcon, XCircleIcon, PrinterIcon } from '@heroicons/react/24/outline';

export default function BillView() {
    const { token } = useParams();
    const [bill, setBill] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchBill = async () => {
            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:6000/api/v1';
                const res = await axios.get(`${apiUrl}/secure-links/verify/${token}`);
                setBill(res.data.data.bill);
            } catch (err) {
                setError(err.response?.data?.message || 'Invalid or expired secure link');
            } finally {
                setLoading(false);
            }
        };
        fetchBill();
    }, [token]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500 mb-4"></div>
                    <p className="text-sm font-medium">Verifying secure link...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white p-4">
                <div className="max-w-md w-full bg-slate-800 border border-slate-700 rounded-lg p-6 text-center shadow-xl">
                    <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold mb-2">Access Denied</h2>
                    <p className="text-slate-400 text-sm mb-6">{error}</p>
                    <a
                        href="/"
                        className="inline-block bg-indigo-650 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded transition"
                    >
                        Go to Portal
                    </a>
                </div>
            </div>
        );
    }

    const getStatusStamp = () => {
        if (bill.status === 'paid') {
            return (
                <span className="bg-green-500/10 text-green-400 border border-green-500/25 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
                    Paid
                </span>
            );
        }
        if (bill.status === 'cancelled') {
            return (
                <span className="bg-red-500/10 text-red-400 border border-red-500/25 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
                    Cancelled
                </span>
            );
        }
        return (
            <span className="bg-amber-500/10 text-amber-400 border border-amber-500/25 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
                Unpaid
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-slate-900 text-white py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto bg-slate-800 border border-slate-700 rounded-lg overflow-hidden shadow-2xl relative">

                {/* Printable Section */}
                <div className="p-8 space-y-8 print:bg-white print:text-black">

                    {/* Header */}
                    <div className="flex justify-between items-start border-b border-slate-700 pb-6 print:border-slate-300">
                        <div>
                            <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400 print:text-black">
                                XPERT PAYMENTS
                            </h1>
                            <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider print:text-slate-500">
                                Digital Billing Systems
                            </p>
                        </div>
                        <div className="text-right">
                            <h2 className="text-lg font-bold text-slate-200 print:text-black">Payment Challan</h2>
                            <p className="text-xs font-mono text-slate-400 mt-1 print:text-slate-500 font-bold">{bill.billId}</p>
                        </div>
                    </div>

                    {/* Status stamp overlay for print/screen */}
                    <div className="flex justify-between items-center bg-slate-850/50 p-4 rounded-lg border border-slate-700 print:border-slate-300 print:bg-slate-50">
                        <div className="space-y-1">
                            <span className="text-xs text-slate-400 block">Challan Status</span>
                            {getStatusStamp()}
                        </div>
                        <button
                            onClick={() => window.print()}
                            className="btn-secondary text-xs flex items-center gap-1.5 no-print"
                        >
                            <PrinterIcon className="h-4 w-4" />
                            Print Challan
                        </button>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="border border-slate-700 rounded-lg p-5 bg-slate-850 print:border-slate-300 print:bg-slate-50">
                            <h3 className="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-3 border-b border-slate-700 pb-1.5 print:text-black print:border-slate-300">
                                Consumer Info
                            </h3>
                            <table className="w-full text-sm space-y-2">
                                <tbody>
                                    <tr>
                                        <td className="text-slate-400 py-1 print:text-slate-500">Consumer Name:</td>
                                        <td className="font-semibold text-right py-1 text-slate-200 print:text-black">{bill.consumerName}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-slate-400 py-1 print:text-slate-500">Billing Month:</td>
                                        <td className="font-semibold text-right py-1 text-slate-200 print:text-black">{bill.billingMonth}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="border border-slate-700 rounded-lg p-5 bg-slate-850 print:border-slate-300 print:bg-slate-50">
                            <h3 className="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-3 border-b border-slate-700 pb-1.5 print:text-black print:border-slate-300">
                                Challan Info
                            </h3>
                            <table className="w-full text-sm space-y-2">
                                <tbody>
                                    <tr>
                                        <td className="text-slate-400 py-1 print:text-slate-500">Challan No:</td>
                                        <td className="font-bold text-right py-1 font-mono text-slate-200 print:text-black text-base">{bill.challanNo}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-slate-400 py-1 print:text-slate-500">Due Date:</td>
                                        <td className="font-semibold text-right py-1 text-slate-200 print:text-black">
                                            {new Date(bill.dueDate).toLocaleDateString()}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pricing table */}
                    <div className="border border-slate-700 rounded-lg overflow-hidden print:border-slate-300">
                        <table className="w-full text-left text-sm border-collapse">
                            <thead>
                                <tr className="bg-slate-850 border-b border-slate-700 print:bg-slate-100 print:border-slate-300">
                                    <th className="py-2.5 px-4 font-bold text-slate-350 print:text-black">Payment Details</th>
                                    <th className="py-2.5 px-4 font-bold text-slate-350 text-right print:text-black">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700 print:divide-slate-300">
                                <tr>
                                    <td className="py-3 px-4 text-slate-300 print:text-black">
                                        Payable Before Due Date
                                    </td>
                                    <td className="py-3 px-4 text-right font-bold text-green-400 print:text-black">
                                        PKR {parseFloat(bill.feeBeforeDueDate).toLocaleString()}
                                    </td>
                                </tr>
                                <tr className="bg-rose-500/5 print:bg-red-50">
                                    <td className="py-3 px-4 text-slate-300 print:text-black">
                                        Payable After Due Date
                                    </td>
                                    <td className="py-3 px-4 text-right font-bold text-rose-400 print:text-black">
                                        PKR {parseFloat(bill.feeAfterDueDate).toLocaleString()}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* QR payment directions */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center border-t border-slate-700 pt-6 print:border-slate-300 bg-slate-850/30 p-4 rounded-lg">
                        <div className="md:col-span-2 space-y-2">
                            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider print:text-black">
                                How to Pay digitally
                            </h4>
                            <p className="text-xs text-slate-400 leading-relaxed print:text-slate-600">
                                Raast P2M (Person-to-Merchant) QR codes allow customers in Pakistan to make instant, zero-fee digital payments directly from their bank accounts to merchants. To pay, simply open your bank's mobile app, scan the merchant's QR code, and authorize the transfer.
                            </p>
                            <ul className="list-disc pl-4 text-xs text-slate-500 space-y-1 print:text-slate-600">
                                <li>Log in to your banking application or mobile wallet.</li>
                                <li>Activate your app's built-in QR Scanner.</li>
                                <li>Securely scan the QR code on the right.</li>
                                <li>Verify billing details and finalize the payment.</li>
                            </ul>
                        </div>
                        <div className="text-center md:border-l md:border-slate-700 md:pl-6 print:border-slate-300">
                            {bill.qrCode ? (
                                <>
                                    <img
                                        src={bill.qrCode}
                                        alt="Payment QR"
                                        className="mx-auto w-32 h-32 mb-1 border-2 border-slate-700 p-1 bg-white rounded-md print:border-slate-300"
                                    />
                                    <span className="text-[10px] text-slate-400 uppercase tracking-wider block print:text-slate-500">
                                        Scan with any Bank App
                                    </span>
                                </>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-32 border border-dashed border-slate-650 rounded-md print:border-slate-300">
                                    <QrCodeIcon className="h-8 w-8 text-slate-500 mb-1" />
                                    <span className="text-[10px] text-slate-550">No QR Code Available</span>
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
